/**
 * Globos de notificación animados (reemplazo de alert() nativo).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
  keyframes,
} from '@mui/material';
import {
  CheckCircleRounded,
  CloseRounded,
  ErrorRounded,
  InfoRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  installAlertAsNotify,
  notify as emitNotify,
  registerNotifyListener,
  type NotifyPayload,
  type NotifySeverity,
} from '../utils/notify';

interface NotificationContextType {
  notify: typeof emitNotify;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const popIn = keyframes`
  0% { opacity: 0; transform: translateY(18px) scale(0.92); }
  60% { opacity: 1; transform: translateY(-4px) scale(1.02); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const floaty = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const SEVERITY_UI: Record<
  NotifySeverity,
  { icon: React.ReactNode; bg: string; border: string; accent: string; title: string }
> = {
  success: {
    icon: <CheckCircleRounded sx={{ fontSize: 26 }} />,
    bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 55%, #ffffff 100%)',
    border: 'rgba(16, 185, 129, 0.35)',
    accent: '#059669',
    title: 'Listo',
  },
  error: {
    icon: <ErrorRounded sx={{ fontSize: 26 }} />,
    bg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 55%, #ffffff 100%)',
    border: 'rgba(239, 68, 68, 0.35)',
    accent: '#dc2626',
    title: 'Atención',
  },
  warning: {
    icon: <WarningAmberRounded sx={{ fontSize: 26 }} />,
    bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 55%, #ffffff 100%)',
    border: 'rgba(245, 158, 11, 0.4)',
    accent: '#d97706',
    title: 'Revisar',
  },
  info: {
    icon: <InfoRounded sx={{ fontSize: 26 }} />,
    bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 55%, #ffffff 100%)',
    border: 'rgba(59, 130, 246, 0.35)',
    accent: '#2563eb',
    title: 'Aviso',
  },
};

function BalloonToast({
  item,
  onClose,
}: {
  item: NotifyPayload;
  onClose: (id: string) => void;
}) {
  const ui = SEVERITY_UI[item.severity];
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const ms = item.durationMs ?? (item.severity === 'error' ? 7000 : 4500);
    const t = window.setTimeout(() => setOpen(false), ms);
    return () => window.clearTimeout(t);
  }, [item.durationMs, item.severity, item.id]);

  return (
    <Collapse in={open} onExited={() => onClose(item.id)} timeout={280}>
      <Box
        role="status"
        sx={{
          width: { xs: 'min(92vw, 380px)', sm: 380 },
          borderRadius: '22px',
          border: `1px solid ${ui.border}`,
          background: ui.bg,
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.14), 0 2px 8px rgba(15, 23, 42, 0.06)',
          px: 1.75,
          py: 1.5,
          mb: 1.25,
          animation: `${popIn} 0.42s cubic-bezier(0.22, 1, 0.36, 1), ${floaty} 3.2s ease-in-out 0.42s infinite`,
          backdropFilter: 'blur(8px)',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            backdropFilter: 'none',
          },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              color: ui.accent,
              mt: 0.15,
              flexShrink: 0,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.08))',
            }}
          >
            {ui.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: ui.accent, lineHeight: 1.2, mb: 0.35 }}
            >
              {item.title || ui.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.primary', lineHeight: 1.45, wordBreak: 'break-word' }}
            >
              {item.message}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Cerrar notificación"
            onClick={() => setOpen(false)}
            sx={{ color: 'text.secondary', mt: -0.25, mr: -0.5, minWidth: 40, minHeight: 40 }}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    </Collapse>
  );
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<NotifyPayload[]>([]);

  const push = useCallback((payload: NotifyPayload) => {
    setItems((prev) => [...prev.slice(-4), payload]);
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  useEffect(() => {
    registerNotifyListener(push);
    const uninstall = installAlertAsNotify();
    return () => {
      registerNotifyListener(null);
      uninstall();
    };
  }, [push]);

  const value = useMemo(() => ({ notify: emitNotify }), []);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 8, sm: 20 },
          bottom: { xs: 12, sm: 24 },
          zIndex: 10000,
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        <Stack alignItems="flex-end">
          {items.map((item) => (
            <BalloonToast key={item.id} item={item} onClose={remove} />
          ))}
        </Stack>
      </Box>
    </NotificationContext.Provider>
  );
};

export function useNotify() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotify debe usarse dentro de NotificationProvider');
  return ctx;
}
